import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react'
import { Card, Modal } from 'react-bootstrap'
import moment from 'moment'
import { ChevronDown, ChevronUp } from 'react-feather'
import useCallApi from '../../hooks/useCallApi'
import callAPI from '../../helpers/callAPI'
import Loading from '../../templates/UI/Loading/Loading'
import { useAuth, useProtectedPage } from '../../context/AuthContext'
import { SystemMessageContext } from '../../context/SystemMessageContext'
import ReportTable from '../../templates/ReportTable/ReportTable'
import DateRangeFilter from '../Reports/Filters/DateRangeFilter/DateRangeFilter'
import useDateRangeFilter from '../../hooks/useDateRangeFilter'

const DbJobHistory = () => {
    const auth = useAuth()
    useProtectedPage(auth.permissions, 'cto_dashboard')

    const { showSystemMessage } = useContext(SystemMessageContext)
    const [jobName, setJobName] = useState('')
    const [logModalOpen, setLogModalOpen] = useState(false)
    const [selectedJobForLog, setSelectedJobForLog] = useState(null)
    const [logData, setLogData] = useState([])
    const [logLoading, setLogLoading] = useState(false)
    const [expandedCycles, setExpandedCycles] = useState(() => new Set())
    const { selectedStartDate, selectedEndDate, quickDate, handleChangeStart, handleChangeEnd, handleQuickDate } = useDateRangeFilter('month', true)
    const { data, dataLoaded, refetch } = useCallApi('reports/dbJobReport', 'GET', {}, [], showSystemMessage)

    const openLogModal = useCallback((job) => {
        setSelectedJobForLog({ jobId: job.job_id, jobName: job.job_name })
        setLogModalOpen(true)
        setLogData([])
    }, [])

    const closeLogModal = useCallback(() => {
        setLogModalOpen(false)
        setSelectedJobForLog(null)
        setLogData([])
        setExpandedCycles(new Set())
    }, [])

    useEffect(() => {
        if (!logModalOpen || !selectedJobForLog) return
        setLogLoading(true)
        callAPI(`reports/dbJobReport/log/${selectedJobForLog.jobId}`, 'GET')
            .then((result) => {
                if (result.success && Array.isArray(result.log)) {
                    setLogData(result.log)
                } else {
                    showSystemMessage?.('error', result.errors?.message || 'Failed to load job log')
                    setLogData([])
                }
            })
            .catch(() => {
                showSystemMessage?.('error', 'Failed to load job log')
                setLogData([])
            })
            .finally(() => setLogLoading(false))
    }, [logModalOpen, selectedJobForLog?.jobId])

    document.title = 'DB Jobs Log'

    //** Strip "MB - Daily - " / "MB - Monthly - " etc.
    const formatDbJobName = (name) => {
        if (!name || typeof name !== 'string') return name
        const sep = ' - '
        const i = name.indexOf(sep)
        if (i === -1) return name
        const j = name.indexOf(sep, i + sep.length)
        if (j === -1) return name
        return name.slice(j + sep.length).trim()
    }

    const jobs = Array.isArray(data?.jobs) ? data.jobs : []
    const filteredJobs = jobName ? jobs.filter((j) => j.job_name === jobName) : jobs

    const getStatusClass = (status) => {
        if (!status) return ''
        if (status === 'Error' || status === 'Fail') return 'badge badge-danger'
        if (status === 'started') return 'badge badge-warning'
        if (status === 'finished' || status === 'Success') return 'badge badge-success'
        if (status === 'Note') return 'badge badge-warning'
        return ''
    }

    // Sort steps numerically, but always place step_id=0 (final step) last.
    const stepIdRank = (stepId) => {
        const n = Number(stepId)
        if (!Number.isFinite(n)) return Number.MAX_SAFE_INTEGER
        return n === 0 ? Number.MAX_SAFE_INTEGER : n
    }

    const columns = [
        { Header: 'Job', accessor: 'job_name', Cell: ({ cell: { value } }) => formatDbJobName(value) },
        { Header: 'Frequency', accessor: 'job_frequency', Cell: ({ cell: { value } }) => value || '—' },
        { Header: 'Critical', accessor: 'critical', Cell: ({ cell: { value } }) => (value === 'Y' ? 'Y' : '—') },
        {
            Header: 'Status',
            accessor: (row) => (row.last_run && row.last_run.job_status) || '',
            Cell: ({ row }) => {
                const status = row.original.last_run?.job_status
                return <span className={getStatusClass(status)}>{status ?? '—'}</span>
            },
        },
        // {
        //     Header: 'Message',
        //     accessor: (row) => (row.last_run && row.last_run.job_msg) || '',
        //     Cell: ({ cell: { value } }) => value || '—',
        // },
        {
            Header: 'Last Run Start',
            accessor: (row) => (row.last_run && row.last_run.job_start) || '',
            Cell: ({ cell: { value } }) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '—'),
        },
        {
            Header: 'Run Duration',
            accessor: (row) => (row.last_run && row.last_run.run_duration) || '',
            Cell: ({ row, cell: { value } }) => {
                const hasAttention = row.original?.last_run?.runtime_attention === true
                if (value == null) return '—'
                return (
                    <span style={hasAttention ? { color: '#dc3545', fontWeight: 600 } : undefined}>
                        {value}
                        {hasAttention ? '  ⚠' : ''}
                    </span>
                )
            },
        },
        {
            Header: 'Last Run End',
            accessor: (row) => (row.last_run && row.last_run.job_end) || '',
            Cell: ({ cell: { value } }) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '—'),
        },
        {
            Header: 'Next Run',
            accessor: 'next_scheduled_run_date',
            Cell: ({ cell: { value } }) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '—'),
        },
        {
            Header: 'Job Log',
            accessor: 'job_id',
            Cell: ({ row }) => (
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openLogModal(row.original)}>
                    View log
                </button>
            ),
        },
    ]

    const toggleCycleExpanded = useCallback((cycle) => {
        const c = Number(cycle)
        if (!Number.isFinite(c)) return
        setExpandedCycles((prev) => {
            const next = new Set(prev)
            if (next.has(c)) next.delete(c)
            else next.add(c)
            return next
        })
    }, [])

    const getJobLogRowProps = useCallback(
        (row) => {
            const cycleIndex = Number.isFinite(row?.original?.run_cycle) ? row.original.run_cycle : 0
            const isOutcome = row?.original?.__isOutcomeRow === true
            const canExpand = row?.original?.__canExpandCycle === true
            const isClickable = isOutcome && canExpand
            return {
                className: cycleIndex % 2 === 0 ? 'db-job-log-cycle-light' : 'db-job-log-cycle-dark',
                ...(isClickable
                    ? {
                          onClick: () => toggleCycleExpanded(row?.original?.run_cycle),
                          title: 'Click to show/hide all steps for this cycle',
                          style: { cursor: 'pointer' },
                      }
                    : null),
            }
        },
        [toggleCycleExpanded]
    )

    const displayedLogData = useMemo(() => {
        if (!Array.isArray(logData) || logData.length === 0) return []

        const cycles = new Map()
        for (const row of logData) {
            const c = Number(row?.run_cycle)
            if (!Number.isFinite(c)) continue
            if (!cycles.has(c)) cycles.set(c, [])
            cycles.get(c).push(row)
        }

        const cycleNumbers = Array.from(cycles.keys()).sort((a, b) => b - a)
        const latestCycle = cycleNumbers.length > 0 ? cycleNumbers[0] : null

        const result = []
        for (const cycle of cycleNumbers) {
            const rows = cycles.get(cycle) || []
            const sorted = [...rows].sort((a, b) => stepIdRank(a.step_id) - stepIdRank(b.step_id))

            if (cycle === latestCycle) {
                for (const r of sorted) result.push({ ...r, __isLatestCycle: true })
                continue
            }

            const outcomeRow = rows.find((r) => Number(r?.step_id) === 0) || sorted[sorted.length - 1] || rows[0]
            const canExpand = rows.length > 1
            const isExpanded = expandedCycles.has(cycle)

            result.push({
                ...outcomeRow,
                __isOutcomeRow: true,
                __canExpandCycle: canExpand,
                __isCycleExpanded: isExpanded,
            })

            if (canExpand && isExpanded) {
                const rest = sorted.filter((r) => r !== outcomeRow)
                for (const r of rest) result.push({ ...r, __isExpandedChildRow: true })
            }
        }

        return result
    }, [logData, expandedCycles])

    const getReportOutput = () => (
        <ReportTable data={filteredJobs} columns={columns} />
    )

    return (
        <>
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">DB Jobs Log</h1>
            </div>
            <Card className="mb-3 col-lg-10 pr-0 pl-0">
                <Card.Header className="d-flex justify-content-start align-items-center">
                    <span className="mr-2">DB Job History</span>
                    <>
                        <div className="mr-4">
                            {dataLoaded && jobs.length > 0 && (
                                <select
                                    className="form-control form-control-sm"
                                    onChange={(e) => setJobName(e.target.value)}
                                    value={jobName}
                                >
                                    <option value="">All Jobs</option>
                                    {jobs.map((job) => (
                                        <option key={job.job_id} value={job.job_name}>
                                            {formatDbJobName(job.job_name)}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <DateRangeFilter
                            timeSelect
                            quickDate={quickDate}
                            interval={60}
                            selectedStartDate={selectedStartDate}
                            selectedEndDate={selectedEndDate}
                            handleChangeStart={handleChangeStart}
                            handleChangeEnd={handleChangeEnd}
                            handleQuickDate={handleQuickDate}
                        />
                        <div className="d-flex align-items-center">
                            <button onClick={refetch} className="btn btn-sm btn-primary">
                                Refresh Report
                            </button>
                        </div>
                    </>
                </Card.Header>
                <div className="table-responsive">
                    {dataLoaded ? (filteredJobs.length === 0 ? <div className="p-3">No data for the current parameters.</div> : getReportOutput()) : <Loading />}
                </div>
            </Card>

            <Modal show={logModalOpen} onHide={closeLogModal} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedJobForLog ? `${selectedJobForLog.jobName} (${selectedJobForLog.jobId})` : ''}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {logLoading ? (
                        <Loading />
                    ) : logData.length === 0 ? (
                        <div className="p-3">No log entries for this job.</div>
                    ) : (
                        <div className="table-responsive db-job-log-cycles">
                            <style>{`
                                .db-job-log-cycles table tbody tr.db-job-log-cycle-light { background-color: #f8f9fa !important; }
                                .db-job-log-cycles table tbody tr.db-job-log-cycle-dark { background-color: #e2e6ea !important; }
                                .db-job-log-cycles table thead th,
                                .db-job-log-cycles table tbody td {
                                    padding-top: 0.55rem !important;
                                    padding-bottom: 0.55rem !important;
                                    line-height: 1.25 !important;
                                }
                            `}</style>
                            <ReportTable
                                data={displayedLogData}
                                columns={[
                                    {
                                        Header: 'Cycle',
                                        accessor: 'run_cycle',
                                        Cell: ({ row, cell: { value } }) => {
                                            const cycle = value != null ? value : '—'
                                            const isOutcome = row?.original?.__isOutcomeRow === true
                                            const canExpand = row?.original?.__canExpandCycle === true
                                            const isExpanded = row?.original?.__isCycleExpanded === true

                                            if (!isOutcome || !canExpand) return cycle

                                            return (
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        toggleCycleExpanded(value)
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault()
                                                            e.stopPropagation()
                                                            toggleCycleExpanded(value)
                                                        }
                                                    }}
                                                    title="Click to show/hide all steps for this cycle"
                                                    style={{
                                                        cursor: 'pointer',
                                                        userSelect: 'none',
                                                        fontWeight: 600,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <span
                                                        aria-hidden="true"
                                                        style={{
                                                            display: 'inline-flex',
                                                            width: 18,
                                                            minWidth: 18,
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                    </span>
                                                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{cycle}</span>
                                                </span>
                                            )
                                        },
                                    },
                                    {
                                        Header: 'Step ID',
                                        accessor: 'step_id',
                                        sortType: (rowA, rowB) => stepIdRank(rowA.original.step_id) - stepIdRank(rowB.original.step_id),
                                        Cell: ({ cell: { value } }) => (value != null ? value : '—'),
                                    },
                                    { Header: 'Step', accessor: 'step_name', Cell: ({ cell: { value } }) => value || '—' },
                                    { Header: 'Message', accessor: 'message', Cell: ({ cell: { value } }) => value || '—' },
                                    { Header: 'Run Date', accessor: 'run_date', Cell: ({ cell: { value } }) => value || '—' },
                                    { Header: 'Duration', accessor: 'run_duration', Cell: ({ cell: { value } }) => value != null ? value : '—' },
                                    { Header: 'DB Date', accessor: 'db_date', Cell: ({ cell: { value } }) => value || '—' },
                                ]}
                                disableFilters
                                getRowProps={getJobLogRowProps}
                            />
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    )
}

export { DbJobHistory }
